<template>
  <div v-bind="$attrs" class="action-header d-flex justify-space-between position-relative" :class="{'center-undo': centerUndo}">
    <div v-if="del" class="position-absolute w-100 h-100 d-flex justify-center revert-block" :class="centerUndo ? 'align-center' : 'align-start'">
      <button type="button" class="revert" @click.stop="revert()">
        {{$t('Вернуть')}} {{time > 0 ? time : ''}}
      </button>
    </div>

    <DragElement size="22" color="BW-400-Border" :custom-class="'header-icon cursor-grab '+ dragClass" :show-drag="showDrag" :left="true"/>

    <div class="have-changes" v-if="change">
      <div class="text">
        {{$t('Изменения не опубликованы')}}
      </div>
    </div>

    <div class="d-flex ga-4 align-center">
      <v-hover>
        <template v-slot:default="{ isHovering, props }">
          <v-icon :icon="MoveUpIcon" size="22" v-bind="props"
                  :color="isHovering ? 'primary' : 'BW-400-Border'" class="header-icon cursor-pointer"
                  :class="{hover: isHovering}"
                  @click="props.onMouseleave();emits('up')"
                  :style="{ visibility: showUp ? 'visible' : 'hidden' }"/>
        </template>
      </v-hover>
      <v-hover>
        <template v-slot:default="{ isHovering, props }">
          <v-icon :icon="MoveDownIcon" size="22" v-bind="props"
                  :color="isHovering ? 'primary' : 'BW-400-Border'" class="header-icon cursor-pointer"
                  :class="{hover: isHovering}"
                  @click="props.onMouseleave();emits('down')"
                  :style="{ visibility: showDown ? 'visible' : 'hidden' }"/>
        </template>
      </v-hover>
      <v-hover>
        <template v-slot:default="{ isHovering, props }">
          <v-icon :icon="TrashIcon" size="22" v-bind="props" :color="isHovering ? 'primary' : 'BW-400-Border'"
                  class="header-icon cursor-pointer" :class="{hover: isHovering}" @click.stop="remove" role="button" :aria-label="$t('Удалить')" tabindex="0" @keydown.enter.stop.prevent="remove" @keydown.space.stop.prevent="remove"/>
        </template>
      </v-hover>
    </div>
  </div>
  <Teleport to="body">
    <DialogKit v-if="confirmOpen" v-model="confirmOpen" @close="confirmOpen = false" overlay-close>
      <div class="remove-confirmation" role="dialog" aria-modal="true" :aria-label="removeTitle" @click.stop @keydown.esc.stop.prevent="confirmOpen = false">
        <v-icon size="40" :icon="InfoIcon" color="#B30320"/>
        <h3>{{ removeTitle }}</h3>
        <p>{{ removeDescription }}</p>
        <v-btn variant="outlined" color="accent" rounded="lg" class="w-100 remove-confirm-button mb-2" @click.stop="startRemove">{{ $t('Удалить') }}</v-btn>
        <v-btn variant="outlined" color="accent" rounded="lg" class="w-100" @click.stop="confirmOpen = false">{{ $t('Отмена') }}</v-btn>
      </div>
    </DialogKit>
  </Teleport>
</template>

<script setup lang="ts">
import MoveUpIcon from "~/assets/icons/edit/moveup.vue";
import MoveDownIcon from "~/assets/icons/edit/movedown.vue";
import TrashIcon from "~/assets/icons/edit/trash.vue";
import DragElement from "@/components/DragElement.vue";
import {ref} from "vue";
import InfoIcon from '~/assets/icons/info.vue'
import {useUndoRemoval} from '@/composables/useUndoRemoval'

defineOptions({inheritAttrs: false})

const props = defineProps({
  showDrag: { type: Boolean },
  showUp: { type: Boolean },
  showDown: { type: Boolean },
  dragClass: { type: String },
  change: { type: Boolean },
  confirmRemove: { type: Boolean },
  removeTitle: { type: String, default: '' },
  removeDescription: { type: String, default: '' },
  undoTimeout: { type: Number },
  centerUndo: { type: Boolean }
});

const emits = defineEmits(["up", "down", "remove", "pending-remove"]);
const confirmOpen = ref(false)

const config = useRuntimeConfig()
const {pending: del, time, start, revert} = useUndoRemoval(() => emits('remove'), value => emits('pending-remove', value),
  () => props.undoTimeout ?? Number(config.public.revertTimeout))
const startRemove = () => {confirmOpen.value = false; start()}
const remove = () => {
  if (del.value) return
  if (props.confirmRemove) confirmOpen.value = true
  else start()
}
</script>

<style scoped lang="sass">
.action-header
  &.center-undo
    position: static !important
    .revert-block
      inset: 0
  .revert-block
    z-index: 3
    .revert
      pointer-events: auto
      padding: 8px 20px 8px 16px
      border-radius: 8px
      background: #E7F3FD
      color: #0D87EF
      text-align: center
      font-size: 16px
      font-weight: 400
      line-height: 24px
      width: 228px
      cursor: pointer

.remove-confirmation
  text-align: center
  padding: 8px 0 16px
  h3
    margin: 8px auto
    max-width: 220px
    color: #363636
    font-size: 20px
    font-weight: 600
    line-height: 24px
  p
    margin: 0 auto 16px
    max-width: 280px
    color: #495057
    font-size: 14px
    line-height: 20px
.remove-confirm-button
  color: #B30320 !important
  border-color: #B30320 !important

.have-changes
  display: flex
  align-items: center
  overflow: hidden
  .text
    color: #B20320
    text-overflow: ellipsis
    font-size: 10px
    font-weight: 400
    line-height: normal
    white-space: nowrap
    overflow: hidden
    width: 100%
</style>
